import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import DashboardLayout from './Layout/DashboardLayout';
import './UserManagement.css';

const SupplierManagement = () => {
  // ── Listing state ──
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // ── Ficha / Edit state ──
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [fichaTab, setFichaTab] = useState('dados');

  const emptyForm = {
    name: '', email: '', phone: '', nif: '', address: '', postalCode: '', city: '', country: 'Portugal',
    contactPerson: '', paymentTerm: 30, notes: '', isActive: true
  };
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { loadSuppliers(); }, []);

  const loadSuppliers = async () => {
    try { 
      const r = await api.get('/billing/suppliers'); 
      setSuppliers(Array.isArray(r.data) ? r.data : []); 
    } catch { 
      toast.error('Erro ao carregar fornecedores'); 
    } finally { 
      setLoading(false); 
    }
  };

  const filteredSuppliers = useMemo(() => {
    return suppliers.filter(s => {
      const m = !search || 
        s.name?.toLowerCase().includes(search.toLowerCase()) || 
        s.email?.toLowerCase().includes(search.toLowerCase()) || 
        s.phone?.includes(search) || 
        s.nif?.includes(search) ||
        s.contactPerson?.toLowerCase().includes(search.toLowerCase());
      return m && (filterStatus === 'all' || (filterStatus === 'active' && s.isActive) || (filterStatus === 'inactive' && !s.isActive));
    });
  }, [suppliers, search, filterStatus]);

  const openCreate = () => { 
    setSelectedSupplier(null); 
    setIsCreating(true); 
    setFormData(emptyForm); 
    setFichaTab('dados'); 
  };

  const openFicha = (supplier) => {
    setSelectedSupplier(supplier); 
    setIsCreating(false); 
    setFichaTab('dados');
    setFormData({ 
      name: supplier.name || '', 
      email: supplier.email || '', 
      phone: supplier.phone || '', 
      nif: supplier.nif || '', 
      address: supplier.address || '', 
      postalCode: supplier.postalCode || '', 
      city: supplier.city || '', 
      country: supplier.country || 'Portugal',
      contactPerson: supplier.contactPerson || '', 
      paymentTerm: supplier.paymentTerm || 30, 
      notes: supplier.notes || '', 
      isActive: supplier.isActive !== undefined ? supplier.isActive : true
    });
  };

  const closeFicha = () => { 
    setSelectedSupplier(null); 
    setIsCreating(false); 
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { 
      toast.error('Nome obrigatório'); 
      return; 
    }
    try {
      if (selectedSupplier) {
        await api.put(`/billing/suppliers/${selectedSupplier.id}`, formData);
        toast.success('Fornecedor atualizado!');
      } else {
        await api.post('/billing/suppliers', formData);
        toast.success('Fornecedor criado!');
      }
      closeFicha(); 
      loadSuppliers();
    } catch (err) { 
      toast.error(err.response?.data?.message || 'Erro ao guardar'); 
    }
  };

  const handleToggleActive = async (supplier) => { 
    try { 
      await api.patch(`/billing/suppliers/${supplier.id}/toggle-active`); 
      toast.success(`${supplier.isActive ? 'Desativado' : 'Ativado'}`); 
      loadSuppliers(); 
    } catch { 
      toast.error('Erro'); 
    } 
  };

  const handleDelete = async (supplier) => { 
    if (!window.confirm(`Eliminar fornecedor "${supplier.name}"? Esta ação não pode ser revertida.`)) return; 
    try { 
      await api.delete(`/billing/suppliers/${supplier.id}`); 
      toast.success('Fornecedor eliminado'); 
      loadSuppliers(); 
    } catch { 
      toast.error('Erro ao eliminar'); 
    } 
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-PT') : '—';
  const totalActive = suppliers.filter(s => s.isActive).length;
  const getSupplierCode = (supplier) => supplier?.code || `SUP${String(supplier?.id || 0).padStart(6, '0')}`;

  const S = {
    fichaContainer: { display: 'flex', background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', minHeight: 'calc(100vh - 200px)', overflow: 'hidden' },
    sidebar: { width: '220px', minWidth: '180px', borderRight: '1px solid #e5e7eb', background: '#fafbfc', padding: '16px 0', flexShrink: 0 },
    sidebarTitle: { padding: '0 16px 12px', fontSize: '13px', fontWeight: '600', color: '#6366f1', borderBottom: '1px solid #e5e7eb', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' },
    sidebarBtn: { display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px', color: '#555', borderLeft: '3px solid transparent', transition: 'all 0.15s' },
    sidebarBtnActive: { background: '#f0f0ff', color: '#6366f1', fontWeight: '600', borderLeftColor: '#6366f1' },
    sidebarBtnLabel: { fontWeight: '600', display: 'block' },
    sidebarBtnDesc: { fontSize: '11px', color: '#999', fontWeight: '400' },
    content: { flex: 1, padding: '28px 32px', overflowY: 'auto' },
    sectionTitle: { fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' },
    sectionDesc: { fontSize: '13px', color: '#94a3b8', marginBottom: '20px' },
    formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' },
    formGridFull: { display: 'grid', gridTemplateColumns: '1fr', gap: '16px', marginBottom: '20px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
    label: { fontSize: '12px', fontWeight: '600', color: '#475569' },
    input: { padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', outline: 'none' },
    select: { padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', outline: 'none', background: '#fff' },
    textarea: { padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', outline: 'none', resize: 'vertical', minHeight: '60px' },
    saveBtn: { padding: '10px 24px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '14px', display: 'inline-flex', alignItems: 'center', gap: '6px' },
    backBtn: { padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' },
  };

  if (loading) return <DashboardLayout userType="admin"><div className="um-loading"><div className="spinner"></div><p>A carregar...</p></div></DashboardLayout>;

  // ╔═══════════ FICHA DO FORNECEDOR ═══════════╗
  if (selectedSupplier || isCreating) {
    const tabs = [
      { id: 'dados', icon: '📋', label: 'Dados Básicos', desc: 'Nome, Contacto' },
      { id: 'fiscal', icon: '🏛️', label: 'Dados Fiscais', desc: 'NIF, Morada' },
      { id: 'comercial', icon: '💼', label: 'Condições', desc: 'Pagamento, Notas' },
    ];

    return (
      <DashboardLayout userType="admin">
        <div className="um-container">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <button onClick={closeFicha} style={S.backBtn}>← Voltar</button>
            <div>
              <h1 style={{ margin: 0, fontSize: '22px', color: '#1e293b' }}>{isCreating ? '➕ Novo Fornecedor' : `🏢 ${selectedSupplier?.name}`}</h1>
              {selectedSupplier && <span style={{ fontSize: '13px', color: '#94a3b8' }}>{selectedSupplier.email} • NIF: {selectedSupplier.nif || '—'}</span>}
            </div>
          </div>

          <div style={S.fichaContainer}>
            <div style={S.sidebar}>
              <div style={S.sidebarTitle}>⚙️ Ficha {isCreating ? 'Novo' : 'Fornecedor'}</div>
              {tabs.map(tab => (
                <button key={tab.id} onClick={() => setFichaTab(tab.id)} style={{ ...S.sidebarBtn, ...(fichaTab === tab.id ? S.sidebarBtnActive : {}) }}>
                  <span style={S.sidebarBtnLabel}>{tab.icon} {tab.label}</span>
                  <span style={S.sidebarBtnDesc}>{tab.desc}</span>
                </button>
              ))}
            </div>

            <div style={S.content}>
              {/* TAB: Dados Básicos */}
              {fichaTab === 'dados' && (<div>
                <h2 style={S.sectionTitle}>📋 Dados Básicos</h2>
                <p style={S.sectionDesc}>Informação principal do fornecedor</p>
                {selectedSupplier && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', background: '#f8fafc', borderRadius: '12px', marginBottom: '24px', border: '1px solid #e2e8f0' }}>
                    <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '22px', fontWeight: '700' }}>🏢</div>
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '17px', color: '#1e293b' }}>{selectedSupplier.name}</div>
                      <div style={{ fontSize: '13px', color: '#64748b' }}>Pré-visualização do fornecedor</div>
                      <div style={{ fontSize: '16px', fontWeight: '800', color: '#1f2937', marginTop: '6px', letterSpacing: '0.2px' }}>Código: {getSupplierCode(selectedSupplier)}</div>
                    </div>
                  </div>
                )}
                <div style={S.formGrid}>
                  <div style={S.formGroup}><label style={S.label}>Nome / Razão Social *</label><input style={S.input} value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Nome completo ou empresa" /></div>
                  <div style={S.formGroup}><label style={S.label}>Email</label><input style={S.input} type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="email@fornecedor.com" /></div>
                  <div style={S.formGroup}><label style={S.label}>Telefone</label><input style={S.input} value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} placeholder="+351 912 345 678" /></div>
                  <div style={S.formGroup}><label style={S.label}>Pessoa de Contacto</label><input style={S.input} value={formData.contactPerson} onChange={e => setFormData({ ...formData, contactPerson: e.target.value })} placeholder="Nome do responsável" /></div>
                </div>
                <button onClick={handleSave} style={S.saveBtn}>💾 Guardar Alterações</button>
              </div>)}

              {/* TAB: Dados Fiscais */}
              {fichaTab === 'fiscal' && (<div>
                <h2 style={S.sectionTitle}>🏛️ Dados Fiscais e Identificação</h2>
                <p style={S.sectionDesc}>NIF, morada e dados de faturação</p>
                <div style={S.formGrid}>
                  <div style={S.formGroup}><label style={S.label}>NIF / NIPC</label><input style={S.input} value={formData.nif} onChange={e => setFormData({ ...formData, nif: e.target.value })} placeholder="Contribuinte" /></div>
                  <div style={S.formGroup}><label style={S.label}>País</label><input style={S.input} value={formData.country} onChange={e => setFormData({ ...formData, country: e.target.value })} placeholder="Portugal" /></div>
                </div>
                <div style={S.formGridFull}><div style={S.formGroup}><label style={S.label}>Morada</label><input style={S.input} value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} placeholder="Rua, número, andar" /></div></div>
                <div style={S.formGrid}>
                  <div style={S.formGroup}><label style={S.label}>Código Postal</label><input style={S.input} value={formData.postalCode} onChange={e => setFormData({ ...formData, postalCode: e.target.value })} placeholder="1234-567" /></div>
                  <div style={S.formGroup}><label style={S.label}>Localidade</label><input style={S.input} value={formData.city} onChange={e => setFormData({ ...formData, city: e.target.value })} placeholder="Cidade" /></div>
                </div>
                <button onClick={handleSave} style={S.saveBtn}>💾 Guardar Alterações</button>
              </div>)}

              {/* TAB: Condições Comerciais */}
              {fichaTab === 'comercial' && (<div>
                <h2 style={S.sectionTitle}>💼 Condições Comerciais</h2>
                <p style={S.sectionDesc}>Prazos de pagamento e observações</p>
                <div style={S.formGrid}>
                  <div style={S.formGroup}>
                    <label style={S.label}>Prazo de Pagamento (dias)</label>
                    <input style={S.input} type="number" min={0} max={365} value={formData.paymentTerm} onChange={e => setFormData({ ...formData, paymentTerm: parseInt(e.target.value) || 0 })} />
                    <span style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>Dias para vencimento de faturas</span>
                  </div>
                  <div style={S.formGroup}>
                    <label style={{ ...S.label, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} style={{ width: '18px', height: '18px' }} />
                      Fornecedor Ativo
                    </label>
                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>Desative para suspender compras</span>
                  </div>
                </div>
                <div style={S.formGridFull}>
                  <div style={S.formGroup}>
                    <label style={S.label}>📝 Observações / Notas Internas</label>
                    <textarea style={S.textarea} rows={5} value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Condições especiais, histórico, notas internas..." />
                  </div>
                </div>
                <button onClick={handleSave} style={S.saveBtn}>💾 Guardar Alterações</button>
              </div>)}
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ╔═══════════ LISTING ═══════════╗
  return (
    <DashboardLayout userType="admin">
      <div className="um-container">
        <div className="um-header">
          <div><h1>🏢 Gestão de Fornecedores</h1><p>Gerir todos os fornecedores e condições comerciais</p></div>
          <button className="um-btn-create" onClick={openCreate}>+ Novo Fornecedor</button>
        </div>
        <div className="um-stats">
          <div className="um-stat-card"><span className="um-stat-num">{suppliers.length}</span><span className="um-stat-label">Total</span></div>
          <div className="um-stat-card active"><span className="um-stat-num">{totalActive}</span><span className="um-stat-label">Ativos</span></div>
          <div className="um-stat-card inactive"><span className="um-stat-num">{suppliers.length - totalActive}</span><span className="um-stat-label">Inativos</span></div>
        </div>
        <div className="um-filters">
          <div className="um-search"><span className="um-search-icon">🔍</span><input type="text" placeholder="Pesquisar nome, email, NIF, contacto..." value={search} onChange={e => setSearch(e.target.value)} /></div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}><option value="all">Todos</option><option value="active">Ativos</option><option value="inactive">Inativos</option></select>
        </div>
        <div className="um-table-wrapper">
          <table className="um-table adm-table">
            <thead><tr><th>Fornecedor</th><th>Contacto</th><th>NIF</th><th>Localidade</th><th>Prazo Pag.</th><th>Estado</th><th>Criado</th><th>Ações</th></tr></thead>
            <tbody>
              {filteredSuppliers.length > 0 ? filteredSuppliers.map(supplier => (
                <tr key={supplier.id} className={!supplier.isActive ? 'um-row-inactive' : ''} style={{ cursor: 'pointer' }} onClick={() => openFicha(supplier)}>
                  <td>
                    <div className="um-user-cell">
                      <div className="um-avatar" style={{ background: '#f59e0b' }}>🏢</div>
                      <div>
                        <strong>{supplier.name}</strong>
                        <small style={{ fontWeight: 700, color: '#475569' }}>Código: {getSupplierCode(supplier)}</small>
                        {supplier.contactPerson && <small>👤 {supplier.contactPerson}</small>}
                      </div>
                    </div>
                  </td>
                  <td data-label="Contacto">
                    <div className="um-contact-cell">
                      {supplier.email && <span>📧 {supplier.email}</span>}
                      {supplier.phone && <span>📱 {supplier.phone}</span>}
                    </div>
                  </td>
                  <td data-label="NIF"><span className="um-profile-badge">{supplier.nif || '—'}</span></td>
                  <td data-label="Localidade">{supplier.city || '—'}</td>
                  <td data-label="Prazo Pag.">{supplier.paymentTerm ? `${supplier.paymentTerm} dias` : '—'}</td>
                  <td data-label="Estado"><span className={`um-status ${supplier.isActive ? 'active' : 'inactive'}`}>{supplier.isActive ? '✅ Ativo' : '🚫 Inativo'}</span></td>
                  <td data-label="Criado"><span className="um-date">{fmtDate(supplier.createdAt)}</span></td>
                  <td className="adm-actions">
                    <div className="um-actions" onClick={e => e.stopPropagation()}>
                      <button className="um-btn-action edit" onClick={() => openFicha(supplier)} title="Ficha">📋</button>
                      <button className={`um-btn-action ${supplier.isActive ? 'deactivate' : 'activate'}`} onClick={() => handleToggleActive(supplier)} title={supplier.isActive ? 'Desativar' : 'Ativar'}>{supplier.isActive ? '🚫' : '✅'}</button>
                      <button className="um-btn-action delete" onClick={() => handleDelete(supplier)} title="Eliminar">🗑️</button>
                    </div>
                  </td>
                </tr>
              )) : <tr><td colSpan="8" className="um-empty">Nenhum fornecedor encontrado</td></tr>}
            </tbody>
          </table>
          <div className="um-table-footer">A mostrar {filteredSuppliers.length} de {suppliers.length} fornecedores</div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SupplierManagement;
